import { useState, useEffect } from 'react';
import { instructorApi } from '../api/instructor';
import webApiClient from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { resolveToId } from '../config/danceStyles';

/**
 * useInstructorProfile Hook - Instructor Profile State Management
 *
 * Centralizes instructor profile state management and API integration
 * for InstructorProfilePage (formerly PersonalDetailsPage)
 *
 * Features:
 * - Profile data state management (personal details + recurring schedule)
 * - API integration (load and save profile)
 * - Publishing mode support (AUTO/MANUAL/HYBRID)
 * - Nested state updates for standardSchedule
 */
const useInstructorProfile = () => {
    const { user } = useAuth();

    // Form state
    const [profileData, setProfileData] = useState({
        // Personal Details
        teachingName: '',
        hourlyRate: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Sydney',
        bio: '',
        dailyMessage: '',
        specialties: [],
        qualifications: '',
        instagram: '',          // instructorProfile.socialLinks.instagram (full URL)
        facebook: '',           // instructorProfile.socialLinks.facebook (full URL)
        website: '',            // instructorProfile.socialLinks.website (full URL)
        isActive: true,
        preferredLocations: [],  // Teaching locations (managed via separate API)
        instructorImageKey: null,        // Actual S3 key for instructor profile photo (from DB)
        instructorImageVersion: null,    // Version timestamp for cache busting
        instructorCoverImageKey: null,   // Separate cover image for booking card background
        instructorCoverVersion: null,    // Version timestamp for cover cache busting

        // Recurring Schedule
        standardSchedule: {
            enabled: false,
            workDays: [],
            startTime: '09:00',
            endTime: '17:00',
            lessonDuration: 60,
            lunchBreak: {
                enabled: false,
                startTime: '12:00',
                endTime: '13:00'
            }
        }
    });

    // UI state
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [hasExistingProfile, setHasExistingProfile] = useState(false);

    // Load existing profile on mount
    useEffect(() => {
        loadProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Load existing instructor profile from API
     */
    const loadProfile = async () => {
        if (!user?.organisationId) {
            setError('Switch to your instructor account to access this page');
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError('');

            const profile = await instructorApi.getProfile(user.organisationId);

            if (profile) {
                // Backend returns nested structure: { profile: { instructorProfile: {...} } }
                const instructorProfile = profile.instructorProfile || {};

                setHasExistingProfile(true);

                // hourlyRate may be the new { amount } object (amount in integer CENTS) or a
                // legacy flat number. Base currency is NOT read here — it lives on the org.
                const hourlyRateValue = instructorProfile.hourlyRate;
                let hourlyRate;
                if (typeof hourlyRateValue === 'object' && hourlyRateValue !== null) {
                    // amount is integer CENTS; the form input is dollars, so convert for display/edit.
                    hourlyRate = hourlyRateValue.amount ? hourlyRateValue.amount / 100 : '';
                } else {
                    hourlyRate = hourlyRateValue || '';
                }

                setProfileData({
                    teachingName: instructorProfile.teachingName || '',
                    hourlyRate: hourlyRate,
                    timezone: instructorProfile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Sydney',
                    bio: instructorProfile.bio || '',
                    dailyMessage: instructorProfile.dailyMessage || '',
                    // Backend stores as 'specializations' but web form uses 'specialties'.
                    // Resolve to canonical ids and de-dupe (defensive against legacy/mixed residue).
                    specialties: [...new Set((instructorProfile.specializations || []).map(s => resolveToId(s) || s))],
                    qualifications: instructorProfile.qualifications || '',
                    instagram: instructorProfile.socialLinks?.instagram || '',
                    facebook: instructorProfile.socialLinks?.facebook || '',
                    website: instructorProfile.socialLinks?.website || '',
                    // isActive = accepting bookings (booking CTA); isSearchable = directory
                    // listing. Two independent flags, both default-visible when unset.
                    isActive: instructorProfile.isAcceptingBookings !== undefined
                        ? instructorProfile.isAcceptingBookings
                        : true,
                    isSearchable: instructorProfile.isSearchable !== undefined
                        ? instructorProfile.isSearchable
                        : true,
                    preferredLocations: instructorProfile.preferredLocations || [],
                    instructorImageKey: instructorProfile.imageKey || null,
                    instructorImageVersion: instructorProfile.imageVersion || null,
                    instructorCoverImageKey: instructorProfile.coverImageKey || null,
                    instructorCoverVersion: instructorProfile.coverVersion || null,
                    standardSchedule: instructorProfile.standardSchedule || {
                        enabled: false,
                        workDays: [],
                        startTime: '09:00',
                        endTime: '17:00',
                        lessonDuration: 60,
                        lunchBreak: {
                            enabled: false,
                            startTime: '12:00',
                            endTime: '13:00'
                        }
                    }
                });
            } else {
                setHasExistingProfile(false);
            }
        } catch (error) {
            console.error('Error loading instructor profile:', error);
            // A not-yet-configured instructor is the EXPECTED state for someone who just
            // turned private lessons on, so it must not surface a red failure toast on a page
            // that is working correctly. Branch on the typed error code, never on message
            // text: the backend emits `userMessage` ("The instructor you're looking for was
            // not found"), not the service's internal string, so any regex over the message
            // is guessing at a value nobody guarantees.
            const notConfigured = error.code === 'INSTRUCTOR_NOT_FOUND' || error.status === 404;
            if (notConfigured) {
                setHasExistingProfile(false);
            } else {
                setError('Failed to load profile. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Handle top-level input changes
     * Used for: hourlyRate, timezone, bio, yearsOfExperience, qualifications, isActive
     */
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setProfileData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear messages on input
        setError('');
        setSuccessMessage('');
    };

    /**
     * Handle nested input changes (for standardSchedule and lunchBreak)
     * Used for: standardSchedule.*, lunchBreak.*
     */
    const handleNestedChange = (path, value) => {
        setProfileData(prev => {
            const newData = { ...prev };
            const pathParts = path.split('.');

            let current = newData;
            for (let i = 0; i < pathParts.length - 1; i++) {
                current = current[pathParts[i]];
            }

            current[pathParts[pathParts.length - 1]] = value;

            return newData;
        });

        // Clear messages on input
        setError('');
        setSuccessMessage('');
    };

    /**
     * Handle array changes (for specialties, workDays, etc.)
     * Used for: specialties, workDays
     */
    const handleArrayChange = (fieldPath, newArray) => {
        handleNestedChange(fieldPath, newArray);
    };

    /**
     * Save instructor profile to API
     * @returns {Promise<boolean>} True if save successful, false otherwise
     */
    // Normalise a pasted/typed link into a clean URL, or null if it can't be salvaged.
    // Empty is allowed (the link is optional). A browser-copied URL passes untouched;
    // a scheme-less value like "instagram.com/x" gets "https://" prepended.
    const normaliseUrl = (value) => {
        const trimmed = (value || '').trim();
        if (!trimmed) return '';
        const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
        try {
            const parsed = new URL(withScheme);
            // Must have a dotted host (rejects "garbage" -> "https://garbage")
            if (!parsed.hostname.includes('.')) return null;
            return withScheme;
        } catch {
            return null;
        }
    };

    const saveProfile = async () => {
        try {
            setIsSaving(true);
            setError('');
            setSuccessMessage('');

            // Exclude fields managed separately or legacy fields
            const {
                preferredLocations,       // Managed via separate location endpoints
                dailyMessage,             // Managed via separate daily-message endpoint
                instructorImageKey,       // Managed via separate photo upload endpoint
                instructorImageVersion,   // Managed via separate photo upload endpoint
                instructorCoverImageKey,  // Managed via separate cover upload endpoint
                instructorCoverVersion,   // Managed via separate cover upload endpoint
                instagram,                // Folded into socialLinks below
                facebook,                 // Folded into socialLinks below
                website,                  // Folded into socialLinks below
                ...profileDataToSave
            } = profileData;

            // Clean up empty values - convert empty strings to null for optional numeric fields
            const cleanedData = {
                ...profileDataToSave,
                // Form input is dollars; backend stores integer CENTS.
                hourlyRate: profileDataToSave.hourlyRate === ''
                    ? null
                    : Math.round(parseFloat(profileDataToSave.hourlyRate) * 100),
                // Rebuild the nested socialLinks object the backend expects.
                // All three fields are always sent (loaded values echoed back) so the
                // backend's per-field dot-notation update preserves each one.
                socialLinks: {
                    instagram: normaliseUrl(instagram),
                    facebook: normaliseUrl(facebook),
                    website: normaliseUrl(website)
                }
            };

            const requestData = {
                organisationId: user.organisationId,
                ...cleanedData
                // Keep web portal field names as-is:
                // - isActive (boolean) - backend service transforms to isAcceptingBookings
                // - specialties (array) - backend service transforms to specializations
            };

            if (hasExistingProfile) {
                // Update existing profile
                await instructorApi.updateProfile(user.organisationId, requestData);
            } else {
                // Initialize new profile
                const response = await webApiClient.post('/api/v2/instructor/manage/profile/initialize', requestData);
                if (!response.data || !response.data.success) {
                    setError('Failed to save profile. Please try again.');
                    return false;
                }
            }

            // Save daily message separately via its dedicated endpoint
            await webApiClient.patch('/api/v2/instructor/manage/profile/daily-message', {
                dailyMessage: dailyMessage || null
            });

            setSuccessMessage('Profile saved successfully!');
            setHasExistingProfile(true);

            // Reload profile to sync with backend
            await loadProfile();

            // Auto-clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);

            return true;
        } catch (error) {
            console.error('Error saving instructor profile:', error);

            // Handle V2 API error format: { success, message, error: { code, message } }
            const errorMessage = error.response?.data?.error?.message
                || error.response?.data?.message
                || error.message
                || 'Failed to save profile. Please try again.';

            setError(errorMessage);
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Validate profile data for a specific accordion section.
     *
     * Each accordion's Save button validates ONLY the fields it actually renders, so
     * saving (e.g.) the Profile Card never fails on a bio or social-link field that
     * lives in another accordion. Field-to-section mapping:
     *   - profileCard: teachingName (optional), specialties, hourlyRate
     *   - about:       bio
     *   - share:       instagram / facebook / website
     *   - locations:   timezone (no required validation)
     * Passing no section validates everything (full-profile / initialize path).
     *
     * @param {('profileCard'|'about'|'share'|'locations')} [section] - accordion being saved
     * @returns {boolean} True if valid, false otherwise
     */
    const validateProfile = (section) => {
        // Clear any previous errors
        setError('');

        const checkProfileCard = !section || section === 'profileCard';
        const checkAbout = !section || section === 'about';
        const checkShare = !section || section === 'share';

        if (checkProfileCard) {
            if (!profileData.hourlyRate || parseFloat(profileData.hourlyRate) <= 0) {
                setError('Please enter a valid hourly rate greater than 0');
                return false;
            }

            if (profileData.specialties.length === 0) {
                setError('Please select at least one dance style specialty');
                return false;
            }
        }

        if (checkAbout) {
            if (!profileData.bio || profileData.bio.trim().length < 50) {
                setError('Please provide a bio of at least 50 characters');
                return false;
            }
        }

        if (checkShare) {
            const linkFields = [
                { label: 'Instagram', value: profileData.instagram },
                { label: 'Facebook', value: profileData.facebook },
                { label: 'Website', value: profileData.website }
            ];
            const invalidLink = linkFields.find(f => normaliseUrl(f.value) === null);
            if (invalidLink) {
                setError(`That ${invalidLink.label} link does not look like a valid web address. Paste the full link from your browser, for example https://yourwebsite.com.`);
                return false;
            }
        }

        return true;
    };

    return {
        // State
        profileData,
        isLoading,
        isSaving,
        error,
        successMessage,
        hasExistingProfile,

        // Functions
        loadProfile,
        saveProfile,
        validateProfile,
        normaliseUrl,
        handleInputChange,
        handleNestedChange,
        handleArrayChange,

        // Setters (for edge cases where direct state update needed)
        setProfileData,
        setError,
        setSuccessMessage
    };
};

export default useInstructorProfile;
